import React from 'react';
import { Bell, Share, Plus, Globe, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Bannière d'activation des notifications push.
 * Détecte la plateforme et affiche :
 * - iOS Safari (hors PWA) : guide Partager → Écran d'accueil
 * - iOS Chrome/Firefox : guide Ouvrir Safari → Partager → Écran d'accueil
 * - Android : guide ⋮ → Ajouter à l'écran d'accueil
 * - Desktop/PWA : bouton Activer direct
 */
const PushActivationBanner = ({ pushSubscribe }) => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isIOSBrowser = isIOS && !isStandalone;
  const isIOSNonSafari = isIOS && /CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const handleActivate = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') {
        const ok = await pushSubscribe();
        if (ok) {
          toast.success('Notifications activées !');
        } else {
          toast.error("Erreur lors de l'activation. Réessayez.", { duration: 5000 });
        }
      } else if (perm === 'denied') {
        toast.error('Notifications bloquées. Allez dans Réglages > Notifications pour les activer.', { duration: 8000 });
      } else {
        toast.info('Appuyez à nouveau sur Activer pour autoriser les notifications.', { duration: 5000 });
      }
    } catch (err) {
      console.error('[Push] Permission error:', err);
      toast.error('Erreur : ' + (err.message || "Impossible d'activer les notifications"), { duration: 5000 });
    }
  };

  if (isIOSNonSafari) {
    return (
      <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
        <div className="space-y-2" data-testid="pwa-guide">
          <p className="text-xs font-bold text-amber-900">Pour recevoir les notifications :</p>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-amber-500 rounded flex items-center justify-center flex-shrink-0"><Globe size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>1.</strong> Ouvrez ce site dans <strong>Safari</strong></p>
          </div>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center flex-shrink-0"><Share size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>2.</strong> Appuyez sur <strong>Partager</strong> <Share className="inline w-3 h-3" /></p>
          </div>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-neutral-800 rounded flex items-center justify-center flex-shrink-0"><Plus size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>3.</strong> <strong>"Sur l'écran d'accueil"</strong></p>
          </div>
        </div>
      </div>
    );
  }

  if (isIOSBrowser) {
    return (
      <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
        <div className="space-y-2" data-testid="pwa-guide">
          <p className="text-xs font-bold text-amber-900">Pour recevoir les notifications :</p>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center flex-shrink-0"><Share size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>1.</strong> Appuyez sur <strong>Partager</strong> <Share className="inline w-3 h-3" /> en bas de l'écran</p>
          </div>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-neutral-800 rounded flex items-center justify-center flex-shrink-0"><Plus size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>2.</strong> Sélectionnez <strong>"Sur l'écran d'accueil"</strong></p>
          </div>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-emerald-500 rounded flex items-center justify-center flex-shrink-0"><Bell size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>3.</strong> Ouvrez l'app puis activez les notifications</p>
          </div>
        </div>
      </div>
    );
  }

  if (isAndroid) {
    return (
      <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
        <div className="space-y-2" data-testid="pwa-guide">
          <p className="text-xs font-bold text-amber-900">Pour recevoir les notifications :</p>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center flex-shrink-0"><MoreVertical size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>1.</strong> Appuyez sur <strong>&#8942;</strong> en haut à droite</p>
          </div>
          <div className="bg-white rounded-lg p-2 flex items-center gap-2">
            <div className="w-6 h-6 bg-neutral-800 rounded flex items-center justify-center flex-shrink-0"><Plus size={12} className="text-white" /></div>
            <p className="text-[11px] text-neutral-700"><strong>2.</strong> <strong>"Ajouter à l'écran d'accueil"</strong></p>
          </div>
        </div>
      </div>
    );
  }

  // Desktop ou PWA : bouton Activer direct
  return (
    <div className="px-3 py-2.5 bg-amber-50 border-b border-amber-200">
      <div className="flex items-center gap-2">
        <div className="relative flex h-3 w-3 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </div>
        <p className="text-xs text-amber-800 font-medium flex-1">Activez les notifications push</p>
      </div>
      <button
        className="mt-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg w-full font-medium transition-colors"
        data-testid="activate-push-btn"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={handleActivate}
      >
        Activer
      </button>
    </div>
  );
};

export default PushActivationBanner;
